"use client";

import { useServerErrorMessage } from "@/lib/i18n/use-server-error-message";
import { sanitizeVisibleAssistantText } from "../shared/assistant-visible-text-sanitize";
import type { UIMessage } from "@convex-dev/agent";
import { useUIMessages } from "@convex-dev/agent/react";
import { api } from "@v1/backend/convex/_generated/api";
import { useMutation } from "convex/react";
import { useTranslations } from "next-intl";
import { type ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { persistVisitorToken, readStoredVisitorToken } from "../customer-chat/customer-chat-session-storage";
import { MessengerAgentAvatar } from "./messenger-avatars";
import { type MessengerBubbleItem, MessengerBubbles } from "./messenger-bubbles";
import { MessengerComposer } from "./messenger-composer";
import { useMessenger } from "./messenger-context";
import { MessengerDetailHeader } from "./messenger-header";
import { MessengerLoadOlder, MessengerTypingBubble } from "./messenger-thread-parts";
import { isFullUIMessage } from "./messenger-ui-message";
import { useMessengerStickToBottom } from "./use-messenger-scroll";

const PAGE_SIZE = 30;

interface VisitorSession {
  readonly threadId: string;
  readonly token: string;
}

function toBubble(message: UIMessage): MessengerBubbleItem | null {
  if (message.role !== "user" && message.role !== "assistant") return null;
  const text = (message.role === "assistant" ? sanitizeVisibleAssistantText(message.text) : message.text).trim();
  if (text.length === 0) return null;
  return {
    id: message.key,
    author: message.role === "user" ? "self" : "agent",
    name: null,
    text,
    at: message.status === "streaming" || message.status === "pending" ? null : message._creationTime,
  };
}

function useVisitorOrg(): { readonly orgSlug: string; readonly orgName: string } {
  const { audience } = useMessenger();
  if (audience.kind !== "visitor") throw new Error("visitor chat rendered for a member");
  return audience;
}

function VisitorMessages({
  orgSlug,
  session,
  welcome,
}: {
  readonly orgSlug: string;
  readonly session: VisitorSession;
  readonly welcome: readonly MessengerBubbleItem[];
}): ReactElement {
  const { results, status, loadMore } = useUIMessages(
    api.customerChat.public_messages.listThreadMessages,
    { threadId: session.threadId, visitor_token: session.token, org_slug: orgSlug },
    { initialNumItems: PAGE_SIZE, stream: true },
  );
  const items = useMemo(
    (): MessengerBubbleItem[] => [
      ...welcome,
      ...results
        .filter(isFullUIMessage)
        .map(toBubble)
        .filter((m): m is MessengerBubbleItem => m !== null),
    ],
    [results, welcome],
  );
  const lastRow = results[results.length - 1];
  const last = lastRow && isFullUIMessage(lastRow) ? lastRow : undefined;
  const awaiting = last?.role === "user" || (last?.role === "assistant" && last.text.trim() === "");
  const scrollRef = useMessengerStickToBottom(`${items.length}:${items[items.length - 1]?.text.length ?? 0}:${awaiting}`);
  return (
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      {status === "CanLoadMore" ? <MessengerLoadOlder onClick={() => loadMore(PAGE_SIZE)} /> : null}
      <MessengerBubbles items={items} footer={awaiting ? <MessengerTypingBubble /> : null} />
    </div>
  );
}

/** Help-center visitor: one anonymous chat, kept by a token in this browser. `askText` is sent once on open. */
export function MessengerVisitorChatView({ askText }: { readonly askText: string | null }): ReactElement {
  const t = useTranslations("messenger.thread");
  const errorMessage = useServerErrorMessage();
  const { config } = useMessenger();
  const { orgSlug, orgName } = useVisitorOrg();
  const initSession = useMutation(api.customerChat.public_widget.initSession);
  const sendMessage = useMutation(api.customerChat.public_messages.sendMessage);
  const [session, setSession] = useState<VisitorSession | null>(null);

  // A visitor who wrote before sees their chat again.
  useEffect(() => {
    const token = readStoredVisitorToken(orgSlug);
    if (token === null) return;
    let cancelled = false;
    void initSession({ org_slug: orgSlug, visitor_token: token })
      .then((result) => {
        if (!cancelled) setSession({ threadId: result.thread_id, token: result.visitor_token });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [initSession, orgSlug]);

  const welcome = useMemo(
    (): MessengerBubbleItem[] =>
      config.welcome_message.length > 0
        ? [{ id: "welcome", author: "agent", name: null, text: config.welcome_message, at: null }]
        : [],
    [config.welcome_message],
  );

  const [sendingText, setSendingText] = useState<string | null>(null);
  const send = async (text: string): Promise<boolean> => {
    setSendingText(text);
    try {
      let current = session;
      if (current === null) {
        const result = await initSession({
          org_slug: orgSlug,
          visitor_token: readStoredVisitorToken(orgSlug) ?? undefined,
        });
        persistVisitorToken(orgSlug, result.visitor_token);
        current = { threadId: result.thread_id, token: result.visitor_token };
        setSession(current);
      }
      await sendMessage({ org_slug: orgSlug, thread_id: current.threadId, visitor_token: current.token, text });
      return true;
    } catch (error) {
      toast.error(errorMessage(error, t("fehler")));
      return false;
    } finally {
      setSendingText(null);
    }
  };

  const sendRef = useRef(send);
  sendRef.current = send;
  const asked = useRef<boolean>(false);
  useEffect(() => {
    if (askText === null || asked.current) return;
    asked.current = true;
    void sendRef.current(askText);
  }, [askText]);

  return (
    <>
      <MessengerDetailHeader title={orgName} leading={<MessengerAgentAvatar size="md" />} />
      {session === null ? (
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <MessengerBubbles
            items={
              sendingText === null
                ? welcome
                : [...welcome, { id: "sending", author: "self", name: null, text: sendingText, at: null }]
            }
            footer={sendingText === null ? null : <MessengerTypingBubble />}
          />
        </div>
      ) : (
        <VisitorMessages orgSlug={orgSlug} session={session} welcome={welcome} />
      )}
      <MessengerComposer autoFocus onSend={send} />
    </>
  );
}
